import { useEffect, useState } from 'react';
import {
  getMaklerProfileAdmin,
  updateMaklerProfile,
  addTimelineEvent,
  deleteTimelineEvent,
} from '../services/jrOverview.js';

const KIND = { upcoming: 'Co čeká', history: 'Historie' };
const dateOnly = (v) => (v ? String(v).slice(0, 10) : '');

// JR/JCH editor for the Makler's cohort, huddle/review, exam date and the
// journey timeline shown in the mobile Profil / Cesta screens.
export default function MaklerProfileEditor({ maklerId }) {
  const [profile, setProfile] = useState({ cohort: '', huddle_info: '', review_info: '', exam_date: '', phone: '' });
  const [events, setEvents] = useState([]);
  const [ev, setEv] = useState({ kind: 'upcoming', event_date: '', title: '', description: '' });
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const d = await getMaklerProfileAdmin(maklerId);
      setProfile({
        cohort: d.profile.cohort || '',
        huddle_info: d.profile.huddle_info || '',
        review_info: d.profile.review_info || '',
        exam_date: dateOnly(d.profile.exam_date),
        phone: d.profile.phone || '',
      });
      setEvents(d.events);
    } catch (e) {
      setError(e.message || 'Profil se nepodařilo načíst.');
    }
  };

  useEffect(() => {
    load();
  }, [maklerId]);

  const run = async (fn, ok) => {
    setError('');
    setMsg('');
    try {
      await fn();
      setMsg(ok);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Uložení se nezdařilo.');
    }
  };

  const saveProfile = (e) => {
    e.preventDefault();
    run(() => updateMaklerProfile(maklerId, profile), 'Údaje uloženy.');
  };

  const addEvent = (e) => {
    e.preventDefault();
    run(async () => {
      await addTimelineEvent(maklerId, ev);
      setEv({ kind: ev.kind, event_date: '', title: '', description: '' });
    }, 'Událost přidána.');
  };

  const field = (key, label, type = 'text') => (
    <label className="auth__label">
      {label}
      <input
        className="auth__input"
        type={type}
        value={profile[key]}
        onChange={(e) => setProfile((p) => ({ ...p, [key]: e.target.value }))}
      />
    </label>
  );

  return (
    <>
      {error && <p className="dashboard__error" role="alert">{error}</p>}
      {msg && <p className="dashboard__status" role="status">{msg}</p>}

      <form className="card" onSubmit={saveProfile}>
        <h2 className="card__title">Údaje v profilu makléře</h2>
        <div className="auth__row">
          {field('cohort', 'Kohorta (např. Květen 2026)')}
          {field('exam_date', 'Termín zkoušky', 'date')}
          {field('phone', 'Telefon', 'tel')}
        </div>
        <div className="auth__row">
          {field('huddle_info', 'Huddle (např. Rozjezd A, po a st 8:30)')}
          {field('review_info', 'Review (např. Skupina 1, út 9:00)')}
        </div>
        <button className="button button--spot" type="submit">Uložit údaje</button>
      </form>

      <div className="card">
        <h2 className="card__title">Cesta: co čeká a historie</h2>
        <ul className="jr-list">
          {events.map((x) => (
            <li key={x.id}>
              <strong>{new Date(x.event_date).toLocaleDateString('cs-CZ')}</strong> · {x.title}
              {x.description ? ` – ${x.description}` : ''} <em>({KIND[x.kind]})</em>{' '}
              <button
                type="button"
                className="button button--quiet"
                onClick={() => run(() => deleteTimelineEvent(x.id), 'Událost smazána.')}
              >
                Smazat
              </button>
            </li>
          ))}
          {events.length === 0 && <li>Zatím žádné události.</li>}
        </ul>

        <form onSubmit={addEvent}>
          <div className="auth__row">
            <label className="auth__label">
              Druh
              <select
                className="auth__input"
                value={ev.kind}
                onChange={(e) => setEv({ ...ev, kind: e.target.value })}
              >
                <option value="upcoming">Co čeká</option>
                <option value="history">Historie</option>
              </select>
            </label>
            <label className="auth__label">
              Datum
              <input
                className="auth__input"
                type="date"
                required
                value={ev.event_date}
                onChange={(e) => setEv({ ...ev, event_date: e.target.value })}
              />
            </label>
          </div>
          <label className="auth__label">
            Název
            <input
              className="auth__input"
              required
              maxLength={160}
              value={ev.title}
              onChange={(e) => setEv({ ...ev, title: e.target.value })}
            />
          </label>
          <label className="auth__label">
            Popis (nepovinný)
            <input
              className="auth__input"
              maxLength={500}
              value={ev.description}
              onChange={(e) => setEv({ ...ev, description: e.target.value })}
            />
          </label>
          <button className="button button--spot" type="submit">Přidat událost</button>
        </form>
      </div>
    </>
  );
}
